import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { getActiveCampaigns } from '../../api/campaign';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdVisibility, MdEdit } from 'react-icons/md';
import Chip from '../../components/Chip';

const ActiveCampaigns = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setHeaderTitle('Active Campaigns');
    fetchCampaigns();
  }, [setHeaderTitle]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getActiveCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching active campaigns:', error);
      toast.error('Failed to load active campaigns');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = campaigns.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(campaigns.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!campaigns || campaigns.length === 0) {
    return (
      <section className='flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-sm max-md:max-w-full'>
        <div className='flex flex-col pr-px w-full max-md:max-w-full'>
          <div className='flex items-center justify-between pb-3 w-full pr-5'>
            <h1 className='self-start text-base font-bold pl-6'>Active Campaigns</h1>
            <button
              className='font-bold text-sm text-primary hover:text-primaryHoverDark cursor-pointer'
              onClick={() => navigate('/campaigns/new-req')}
            >
              + Create New Request
            </button>
          </div>
        </div>
        <div className='flex flex-col items-center justify-center py-16'>
          <div className='text-center py-8 text-gray-500'>
            <p className='text-lg mb-4'>No Active Campaigns Found</p>
            <button
              onClick={() => navigate('/campaigns/new-req')}
              className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark transition-colors'
            >
              Create Your First Campaign Request
            </button>
          </div>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </section>
    );
  }

  return (
    <section className='flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-sm max-md:max-w-full'>
      <div className='flex flex-col pr-px w-full max-md:max-w-full'>
        <div className='flex items-center justify-between pb-3 w-full pr-5'>
          <h1 className='self-start text-base font-bold pl-6'>List of Active Campaigns</h1>
        </div>

        {/* Table */}
        <div className='overflow-x-auto px-6 pb-3'>
          <table className='w-full border-collapse'>
            <thead>
              <tr className='bg-gray-100 border-b border-gray-200'>
                <th className='text-left text-xs font-bold   text-gray-800  py-3 px-4'>Sr No.</th>
                <th className='text-left text-xs font-bold   text-gray-800  py-3 px-4'>Campaign Name</th>
                <th className='text-left text-xs font-bold   text-gray-800  py-3 px-4'>Campaign Type</th>
                <th className='text-left text-xs font-bold   text-gray-800  py-3 px-4'>Duration</th>
                <th className='text-center text-xs font-bold   text-gray-800  py-3 px-4'>Status</th>
                <th className='text-center text-xs font-bold   text-gray-800  py-3 px-4'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((campaign, index) => (
                <tr key={campaign._id || campaign.id || index} className='border-b border-gray-100'>
                  <td className='py-3 px-4 text-sm text-gray-900'>{indexOfFirstItem + index + 1}</td>
                  <td className='py-3 px-4 text-sm text-gray-900'>
                    {campaign.title || campaign.campaignTitle || 'N/A'}
                  </td>
                  <td className='py-3 px-4 text-sm text-gray-900'>
                    {campaign.campaignType || campaign.type || 'N/A'}
                  </td>
                  <td className='py-3 px-4 text-sm text-gray-900'>
                    {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                  </td>
                  <td className='py-3 px-4 text-center'>
                    <Chip value="Active" color="green" variant="filled" small />
                  </td>
                  <td className='py-3 px-4 text-center'>
                    <button
                      className='inline-flex items-center justify-center p-1 hover:opacity-70 transition-opacity'
                      onClick={() => navigate(`/campaigns/details/${campaign._id || campaign.id}`)}
                      title='View Campaign Details'
                    >
                      <MdVisibility className='text-green-600 text-xl' />
                    </button>
                    {/* Edit button removed — partners cannot edit campaigns from partner portal */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className='flex items-center justify-between px-6 py-3 border-t border-gray-200'>
          <div className='text-sm text-gray-700'>
            {campaigns.length} Total Rows
          </div>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className={`px-2 py-1 text-xs border border-gray-300 rounded ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              &lt;&lt;
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-2 py-1 text-xs border border-gray-300 rounded ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              &lt;
            </button>
            <span className='text-xs text-gray-700 px-1'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 text-xs border border-gray-300 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              &gt;
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 text-xs border border-gray-300 rounded ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              &gt;&gt;
            </button>
            <span className='text-gray-400 px-1'>|</span>
            <label className='text-xs text-gray-700'>Go to page:</label>
            <input
              type='number'
              min='1'
              max={totalPages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= totalPages) {
                  paginate(page);
                }
              }}
              className='w-12 px-2 py-1 text-xs border border-gray-300 rounded'
            />
            <label className='text-xs text-gray-700 ml-2'>Show</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className='px-2 py-1 text-xs border border-gray-300 rounded'
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
      <ToastContainer position="bottom-right" autoClose={2000} />
    </section>
  );
};

export default ActiveCampaigns;
